import { useEffect, useState } from 'react';
import { getTtfOrderDetails, setTtfLotteryOutcome, setTtfPaymentLink } from '../../services/adminService';
import { formatPrice } from '../../utils/format';
import { toWhatsAppNumber } from '../../utils/ttf';

const cardStyle = {
  background: 'white',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  padding: '1.5rem',
  marginBottom: '1.25rem',
};

const titleStyle = {
  margin: '0 0 1rem', fontSize: '0.85rem', fontWeight: '600', color: '#888',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: '1rem', padding: '0.3rem 0', fontSize: '0.9rem' };

const PAYMENT_LABELS = {
  mercado_pago: 'Tarjeta con liga de Mercado Pago (100%)',
  card: 'Tarjeta (Stripe, pedido anterior)',
  bank_transfer: 'Transferencia (anticipo 50%, opción retirada)',
  bank_transfer_full: 'Transferencia bancaria (100%)',
};
const DELIVERY_LABELS = { pickup: 'Recoger en tienda', delivery: 'Envío a domicilio' };

/**
 * Taipei Toy Festival fields for a source = 'ttf' order: payment terms,
 * contact, refund CLABE, per-line lottery/signature flags, and the lottery
 * outcome buttons ("Ganada" adds the piece to the order total so its payment
 * can be recorded; onOrderChanged lets the page reload the order).
 */
const TtfOrderDetailsCard = ({ token, order, onOrderChanged }) => {
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  const [busyIndex, setBusyIndex] = useState(null);
  const [paymentLink, setPaymentLink] = useState(order?.mercadoPagoUrl || '');
  const [savingLink, setSavingLink] = useState(false);
  const [linkError, setLinkError] = useState(null);

  useEffect(() => { setPaymentLink(order?.mercadoPagoUrl || ''); }, [order?.mercadoPagoUrl]);

  const handleSaveLink = async (e) => {
    e.preventDefault();
    setLinkError(null);
    setSavingLink(true);
    try {
      await setTtfPaymentLink(token, order.id, paymentLink);
      await onOrderChanged?.();
    } catch (err) {
      setLinkError(err.message);
    } finally {
      setSavingLink(false);
    }
  };

  const owed = Math.max(0, Number(order?.totalAmount || 0) - Number(order?.amountPaid || 0));
  const whatsappMessage = order?.mercadoPagoUrl
    ? `Hola ${order.customer?.name || ''}, aquí está tu liga de pago de Mercado Pago por ${formatPrice(owed, 'MXN')} `
      + `para tu pedido #${order.id} de Taipei Toy Festival: ${order.mercadoPagoUrl}`
    : '';

  const handleOutcome = async (lineIndex, outcome) => {
    const item = order.items[lineIndex];
    // Mirrors the server's auto-cancel rule (setTtfLotteryOutcome).
    const willCancel = outcome === 'lost'
      && Number(order.amountPaid || 0) <= 0
      && order.items.every((i, idx) => i.is_lottery && (idx === lineIndex || i.lottery_outcome === 'lost'));
    const question = outcome === 'won'
      ? `¿Marcar "${item.name}" como ganada? Se sumará al total del pedido para registrar su pago.`
      : willCancel
        ? `¿Marcar "${item.name}" como no ganada? El pedido se cancelará porque ya no tiene piezas por cobrar.`
        : `¿Marcar "${item.name}" como no ganada?`;
    if (!window.confirm(question)) return;
    setBusyIndex(lineIndex);
    try {
      await setTtfLotteryOutcome(token, order.id, lineIndex, outcome);
      await onOrderChanged?.();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setBusyIndex(null);
    }
  };

  useEffect(() => {
    if (!token || !order?.id) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getTtfOrderDetails(token, order.id);
        if (!cancelled) setDetails(data.details);
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [token, order?.id]);

  // Keep each line's index in order.items — the API addresses lines by it.
  const lines = (order?.items || [])
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.ttf);

  return (
    <div style={cardStyle}>
      <h3 style={titleStyle}>Taipei Toy Festival</h3>
      {error && <p style={{ color: '#dc3545', fontSize: '0.85rem' }}>{error}</p>}
      {details && (
        <>
          <div style={rowStyle}><span>Pago</span><strong>{PAYMENT_LABELS[details.payment_method] || details.payment_method}</strong></div>
          {/* Only orders placed while the 4% transfer discount existed. */}
          {Number(details.discount_amount) > 0 && (
            <div style={rowStyle}><span>Descuento por pago completo</span><strong>−{formatPrice(Number(details.discount_amount), 'MXN')}</strong></div>
          )}
          <div style={rowStyle}><span>A pagar al pedir</span><strong>{formatPrice(Number(details.amount_due_now), 'MXN')}</strong></div>
          {Number(details.lottery_subtotal) > 0 && (
            <div style={rowStyle}><span>Lotería (no incluida en el total; se cobra solo si se gana)</span><strong>{formatPrice(Number(details.lottery_subtotal), 'MXN')}</strong></div>
          )}
          <div style={rowStyle}>
            <span>Entrega</span>
            <strong>{DELIVERY_LABELS[details.delivery_method] || details.delivery_method}{Number(details.delivery_fee) > 0 ? ` (+${formatPrice(Number(details.delivery_fee), 'MXN')})` : ''}</strong>
          </div>
          {details.instagram && (
            <div style={rowStyle}>
              <span>Instagram</span>
              <a href={`https://instagram.com/${details.instagram}`} target="_blank" rel="noopener noreferrer">@{details.instagram}</a>
            </div>
          )}
          <div style={rowStyle}>
            <span>WhatsApp</span>
            <a href={`https://wa.me/${toWhatsAppNumber(details.whatsapp)}`} target="_blank" rel="noopener noreferrer">{details.whatsapp}</a>
          </div>
          {details.clabe && (
            <div style={rowStyle}><span>CLABE para devolución</span><strong style={{ fontFamily: 'monospace' }}>{details.clabe}</strong></div>
          )}
          {details.clabe_bank && (
            <div style={rowStyle}><span>Banco</span><strong>{details.clabe_bank}</strong></div>
          )}
          {details.clabe_holder && (
            <div style={rowStyle}><span>Titular</span><strong>{details.clabe_holder}</strong></div>
          )}
          {details.stripe_payment_intent_id && (
            <div style={rowStyle}><span>Stripe</span><code style={{ fontSize: '0.8rem' }}>{details.stripe_payment_intent_id}</code></div>
          )}
          <form onSubmit={handleSaveLink} style={{ margin: '0.75rem 0 0.25rem', paddingTop: '0.75rem', borderTop: '1px solid #f0f0f0' }}>
            <label htmlFor="ttf-mp-link" style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '0.3rem' }}>
              Liga de pago de Mercado Pago {owed > 0 ? `(por cobrar ${formatPrice(owed, 'MXN')})` : ''}
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <input
                id="ttf-mp-link"
                type="url"
                value={paymentLink}
                onChange={(e) => setPaymentLink(e.target.value)}
                placeholder="https://mpago.la/…"
                style={{ flex: '1 1 220px', padding: '0.45rem 0.7rem', border: '1px solid #ddd', borderRadius: 8, fontSize: '0.88rem' }}
              />
              <button
                type="submit"
                disabled={savingLink || paymentLink === (order.mercadoPagoUrl || '')}
                style={{ padding: '0.45rem 0.9rem', border: 'none', borderRadius: 8, background: '#2ca9e1', color: 'white', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', opacity: savingLink || paymentLink === (order.mercadoPagoUrl || '') ? 0.5 : 1 }}
              >
                {savingLink ? 'Guardando…' : 'Guardar'}
              </button>
              {order.mercadoPagoUrl && (
                <a
                  href={`https://wa.me/${toWhatsAppNumber(details.whatsapp)}?text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ padding: '0.45rem 0.9rem', borderRadius: 8, background: '#25D366', color: 'white', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}
                >
                  Enviar por WhatsApp
                </a>
              )}
            </div>
            {linkError && <p style={{ color: '#dc3545', fontSize: '0.82rem', margin: '0.35rem 0 0' }}>{linkError}</p>}
            <span style={{ fontSize: '0.75rem', color: '#999' }}>
              Crea la liga en Mercado Pago por el monto por cobrar, pégala aquí y envíala. Cuando la paguen, registra el pago
              (método Mercado Pago) con el formulario de pagos del pedido.
            </span>
          </form>
          <p style={{ fontSize: '0.8rem', color: '#888', margin: '0.5rem 0 0' }}>
            Las piezas se entregan o envían solo con el pedido pagado al 100%. Las devoluciones se hacen por el monto
            completo (la comisión la absorbe Bluppimart). Si se gana una pieza de lotería, márcala como
            "Ganada" abajo y luego registra su pago con el formulario de pagos del pedido.
          </p>
        </>
      )}
      {lines.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '1rem 0 0', borderTop: '1px solid #f0f0f0' }}>
          {lines.map(({ item, index }) => (
            <li key={index} style={{ ...rowStyle, borderBottom: '1px solid #f8f8f8', flexWrap: 'wrap', alignItems: 'center' }}>
              <span>{item.quantity} × {item.name}</span>
              <span style={{ display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: 'wrap' }}>
                {item.is_lottery && <span style={{ background: '#fdecef', color: '#E4405F', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600 }}>Lotería</span>}
                {item.wants_signature && <span style={{ background: '#f3e8ff', color: '#7c3aed', borderRadius: 10, padding: '1px 8px', fontSize: '0.75rem', fontWeight: 600 }}>Firma</span>}
                {item.lottery_outcome === 'won' && <span style={{ color: '#28a745', fontSize: '0.8rem', fontWeight: 600 }}>Ganada</span>}
                {item.lottery_outcome === 'lost' && <span style={{ color: '#888', fontSize: '0.8rem', fontWeight: 600 }}>No ganada</span>}
                {item.is_lottery && !item.lottery_outcome && (
                  <>
                    <button
                      type="button"
                      onClick={() => handleOutcome(index, 'won')}
                      disabled={busyIndex !== null}
                      style={{ background: 'none', border: '1px solid #28a745', color: '#28a745', borderRadius: 8, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      Ganada
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOutcome(index, 'lost')}
                      disabled={busyIndex !== null}
                      style={{ background: 'none', border: '1px solid #ccc', color: '#888', borderRadius: 8, padding: '2px 8px', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >
                      No ganada
                    </button>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TtfOrderDetailsCard;
